import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../services/authService';
import notificationService from '../services/notificationService';
import paymentService from '../services/paymentService';
import hostelService from '../services/hostelService';
import messService from '../services/messService';

// Updated: 2025-12-22 - Added Settings Button
const Layout = ({ children, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = authService.getCurrentUser();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarViewDate, setCalendarViewDate] = useState(new Date());
  const [pendingPaymentsCount, setPendingPaymentsCount] = useState(0);
  const [pendingHostelCount, setPendingHostelCount] = useState(0);
  const [pendingMessCount, setPendingMessCount] = useState(0);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  // Close calendar (and notifications) on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowCalendar(false);
        setShowNotifications(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const buildCalendarDays = (viewDate) => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return { cells, year, month, today };
  };

  const calendarMonthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  // Load notifications and listen to manual refresh events
  useEffect(() => {
    loadNotifications();
    // Refresh notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);

    const handleRefresh = () => {
      loadPendingPaymentsCount();
      loadPendingHostelCount();
      loadPendingMessCount();
      loadNotifications();
    };
    window.addEventListener('refreshPendingCounts', handleRefresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener('refreshPendingCounts', handleRefresh);
    };
  }, []);

  // Load pending payments count
  useEffect(() => {
    loadPendingPaymentsCount();
    const interval = setInterval(loadPendingPaymentsCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadPendingPaymentsCount = async () => {
    try {
      const response = await paymentService.getPaymentsAdmin({ status: 'pending' });
      const payments = response?.data?.data || [];
      setPendingPaymentsCount(payments.length);
    } catch (err) {
      console.error('Error loading pending payments count:', err);
    }
  };

  // Load pending hostel count (pending bookings + pending leaves)
  useEffect(() => {
    loadPendingHostelCount();
    const interval = setInterval(loadPendingHostelCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadPendingHostelCount = async () => {
    try {
      const [bookingsResponse, leavesResponse, admissionsResponse] = await Promise.all([
        hostelService.getBookingRequests(),
        hostelService.getLeaves(),
        hostelService.getAdmissions(),
      ]);
      const pendingBookings = (bookingsResponse?.data?.data || []).filter(b => b.status === 'pending').length;
      const pendingLeaves = (leavesResponse?.data?.data || []).filter(l => l.status === 'pending').length;
      const pendingAdmissions = (admissionsResponse?.data?.data || []).filter(a =>
        ['payment_pending_verification', 'documents_submitted', 'payment_completed'].includes(a.admission_status)
      ).length;
      setPendingHostelCount(pendingBookings + pendingLeaves + pendingAdmissions);
    } catch (err) {
      console.error('Error loading pending hostel count:', err);
    }
  };

  // Load pending mess count (pending bookings + pending leaves)
  useEffect(() => {
    loadPendingMessCount();
    const interval = setInterval(loadPendingMessCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadPendingMessCount = async () => {
    try {
      const [bookingsResponse, leavesResponse] = await Promise.all([
        messService.getBookingRequestsAdmin(),
        messService.getLeavesAdmin(),
      ]);
      const pendingBookings = (bookingsResponse?.data?.data || []).filter(b => b.status === 'pending').length;
      const pendingLeaves = (leavesResponse?.data?.data || []).filter(l => l.status === 'pending').length;
      setPendingMessCount(pendingBookings + pendingLeaves);
    } catch (err) {
      console.error('Error loading pending mess count:', err);
    }
  };

  const loadNotifications = async () => {
    try {
      const [notifResponse, countResponse] = await Promise.all([
        notificationService.getNotifications({ per_page: 10 }),
        notificationService.getUnreadCount(),
      ]);
      setNotifications(notifResponse.data || []);
      setUnreadCount(countResponse.unread_count || 0);
    } catch (err) {
      console.error('Error loading notifications:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await notificationService.markAsRead(notification.id);
      loadNotifications();
    }
    setShowNotifications(false);
    
    // Show notification details modal
    setSelectedNotification(notification);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      loadNotifications();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const formatNotificationTime = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffMs = now - notifTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  const navItems = [
    { title: 'Dashboard', path: '/', icon: '📊' },
    { title: 'Registration Approvals', path: '/registration-approvals', icon: '✅' },
    { title: 'Students', path: '/students', icon: '👥' },
    { title: 'Mess Management', path: '/mess', icon: '🍽️' },
    { title: 'Hostel Management', path: '/hostel', icon: '🏠' },
    { title: 'Payments', path: '/payments', icon: '💰' },
    { title: 'Employee Management', path: '/employee-management', icon: '👔' },
    { title: 'Agreements', path: '/agreements', icon: '📄' },
  ];

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="logo" onClick={() => navigate('/')}>
            <img src="/SaiNiwasPayingGuest.png" alt="Sai Nirvaar Logo" className="logo-icon" />
            <div className="logo-text">
              <h1>Sai Mess</h1>
              <span>& Hostel</span>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <div
              key={item.path}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-text">{item.title}</span>
              {item.path === '/payments' && pendingPaymentsCount > 0 && (
                <span className="nav-badge">{pendingPaymentsCount}</span>
              )}
              {item.path === '/hostel' && pendingHostelCount > 0 && (
                <span className="nav-badge">{pendingHostelCount}</span>
              )}
              {item.path === '/mess' && pendingMessCount > 0 && (
                <span className="nav-badge">{pendingMessCount}</span>
              )}
            </div>
          ))}
        </nav>
        
        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="avatar">{user?.first_name?.charAt(0) || 'A'}</div>
            <div className="user-info">
              <span className="user-name">{user?.first_name || user?.username}</span>
              <span className="user-role">Administrator</span>
            </div>
          </div>
          <div className="sidebar-actions" style={{ display: 'flex', gap: '8px' }}>
            <button className="settings-btn-sidebar" onClick={() => navigate('/settings')} title="Settings" style={{ flex: 1 }}>
              ⚙️ Settings
            </button>
            <button className="logout-btn" onClick={handleLogout} style={{ flex: 1 }}>
              🚪 Logout
            </button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        <header className="main-header">
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate('/')}>←</button>
            <h2>{title}</h2>
          </div>
          <div className="header-right">
            <button 
              className="notification-bell" 
              onClick={() => setShowNotifications(!showNotifications)}
              title="Notifications"
            >
              🔔
              {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </button>
            <div style={{ position: 'relative' }}>
              <button
                className="date-display-btn"
                onClick={() => { setShowCalendar(v => !v); setShowNotifications(false); }}
                title="Toggle Calendar"
              >
                📅 {(() => {
                  const date = new Date();
                  const day = String(date.getDate()).padStart(2, '0');
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const year = date.getFullYear();
                  return `${day}/${month}/${year}`;
                })()}
              </button>
              {showCalendar && (() => {
                const { cells, year, month, today } = buildCalendarDays(calendarViewDate);
                return (
                  <div className="calendar-popup">
                    <div className="calendar-header">
                      <button className="cal-nav-btn" onClick={() => setCalendarViewDate(new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth() - 1, 1))}>‹</button>
                      <span className="cal-month-label">{calendarMonthNames[month]} {year}</span>
                      <button className="cal-nav-btn" onClick={() => setCalendarViewDate(new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth() + 1, 1))}>›</button>
                    </div>
                    <div className="calendar-grid">
                      {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                        <div key={d} className="cal-day-label">{d}</div>
                      ))}
                      {cells.map((day, idx) => {
                        const isToday = day &&
                          today.getDate() === day &&
                          today.getMonth() === month &&
                          today.getFullYear() === year;
                        return (
                          <div
                            key={idx}
                            className={`cal-day${day ? '' : ' cal-day-empty'}${isToday ? ' cal-day-today' : ''}`}
                          >
                            {day || ''}
                          </div>
                        );
                      })}
                    </div>
                    <div className="cal-footer">
                      <button className="cal-today-btn" onClick={() => setCalendarViewDate(new Date())}>Today</button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </header>

        {showNotifications && (
          <div className="notifications-dropdown">
            <div className="notifications-header">
              <h3>Notifications</h3>
              {unreadCount > 0 && (
                <button onClick={handleMarkAllRead} className="mark-all-read-btn">
                  Mark all read
                </button>
              )}
            </div>
            <div className="notifications-list">
              {notifications.length === 0 ? (
                <div className="no-notifications">No notifications</div>
              ) : (
                notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`notification-item ${!notif.is_read ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(notif)}
                    style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <div style={{ flex: 1, display: 'flex', gap: '12px' }}>
                      <div className="notification-icon">
                        {notif.notification_type === 'password_reset' ? '🔑' :
                         notif.notification_type === 'payment_due' ? '💰' :
                         notif.notification_type === 'document_verified' ? '✅' : '🔔'}
                      </div>
                      <div className="notification-content">
                        <div className="notification-title">{notif.title}</div>
                        <div className="notification-message">{notif.message}</div>
                        <div className="notification-time">{formatNotificationTime(notif.created_at)}</div>
                      </div>
                      {!notif.is_read && <div className="unread-dot"></div>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        <div className="page-content">
          {children}
        </div>

        <footer className="main-footer">
          <p>Design and developed by Denebrix Technologies Pvt. Ltd., Nashik. Mob. 8796901315</p>
        </footer>
      </main>

      <style>{`
        .layout {
          display: flex;
          min-height: 100vh;
          background: #f5f7fa;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .sidebar {
          width: 260px;
          background: #ffffff;
          display: flex;
          flex-direction: column;
          position: fixed;
          height: 100vh;
          border-right: 1px solid #e5e7eb;
          z-index: 1000;
          left: 0;
          top: 0;
        }

        .sidebar-header {
          padding: 24px 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
        }

        .logo-icon {
          width: 65px;
          height: 65px;
          object-fit: contain;
          border-radius: 8px;
        }

        .logo-text h1 {
          font-size: 18px;
          font-weight: 700;
          margin: 0;
          color: #1f2937;
        }

        .logo-text span {
          font-size: 12px;
          color: #6b7280;
          font-weight: 700;
        }

        .sidebar-nav {
          flex: 1;
          padding: 16px 12px;
          overflow-y: auto;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          border-radius: 8px;
          cursor: pointer;
          margin-bottom: 4px;
          color: #4b5563;
          font-size: 14px;
        }

        .nav-item:hover {
          background: #f3f4f6;
        }

        .nav-item.active {
          background: #f7fee7;
          color: #65a30d;
        }

        .nav-item.active .nav-text {
          font-weight: 500;
        }

        .nav-icon {
          font-size: 18px;
        }

        .nav-text {
          font-size: 14px;
          flex: 1;
        }

        .nav-badge {
          min-width: 20px;
          height: 20px;
          background: #ef4444;
          color: #ffffff;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 5px;
          margin-left: auto;
          line-height: 1;
        }

        .sidebar-footer {
          padding: 16px 12px;
          border-top: 1px solid #e5e7eb;
        }

        .user-profile {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
          padding: 10px;
          background: #f9fafb;
          border-radius: 8px;
        }

        .avatar {
          width: 36px;
          height: 36px;
          background: #84cc16;
          color: white;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
        }

        .user-info {
          display: flex;
          flex-direction: column;
        }

        .user-name {
          font-size: 13px;
          font-weight: 500;
          color: #1f2937;
        }

        .user-role {
          font-size: 11px;
          color: #6b7280;
        }

        .logout-btn {
          width: 100%;
          padding: 10px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
        }

        .logout-btn:hover {
          background: #fee2e2;
        }

        .settings-btn-sidebar {
          padding: 10px;
          background: #f0f4ff;
          border: 1px solid #bfdbfe;
          color: #1e40af;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .settings-btn-sidebar:hover {
          background: #dbeafe;
          border-color: #60a5fa;
        }

        .sidebar-actions {
          display: flex;
          gap: 8px;
          margin-top: 12px;
        }

        .main-content {
          flex: 1;
          margin-left: 260px;
          padding: 24px 32px;
          overflow-x: auto;
          overflow-y: visible;
        }

        .main-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          padding: 16px 20px;
          background: linear-gradient(135deg, #84cc16 0%, #65a30d 50%, #4d7c0f 100%);
          border-radius: 16px;
          border: none;
          box-shadow: 
            0 10px 25px -5px rgba(101, 163, 13, 0.4),
            0 8px 16px -8px rgba(77, 124, 15, 0.3),
            0 -2px 4px 0px rgba(217, 249, 157, 0.2) inset,
            0 4px 8px -2px rgba(0, 0, 0, 0.15);
          position: relative;
        }

        .main-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 50%;
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, transparent 100%);
          pointer-events: none;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 12px;
          position: relative;
          z-index: 1;
        }

        .back-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.2);
          font-size: 16px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          backdrop-filter: blur(4px);
          transition: all 0.2s ease;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .back-btn:hover {
          background: rgba(255, 255, 255, 0.35);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .header-left h2 {
          font-size: 20px;
          font-weight: 600;
          color: white;
          margin: 0;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 12px;
          position: relative;
          z-index: 1;
        }

        .settings-btn {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.2);
          font-size: 18px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          color: white;
          backdrop-filter: blur(4px);
        }

        .settings-btn:hover {
          background: rgba(255, 255, 255, 0.35);
          transform: rotate(90deg);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .date-display-btn {
          font-size: 13px;
          font-weight: 600;
          color: white;
          padding: 8px 14px;
          background: rgba(255,255,255,0.18);
          border: 2px solid rgba(255,255,255,0.3);
          border-radius: 10px;
          cursor: pointer;
          backdrop-filter: blur(4px);
          transition: background 0.2s ease, border-color 0.2s ease;
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          white-space: nowrap;
        }

        .date-display-btn:hover {
          background: rgba(255,255,255,0.28);
          border-color: rgba(255,255,255,0.5);
        }

        .calendar-popup {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          width: 280px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 40px -10px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.08);
          border: 1px solid #e5e7eb;
          z-index: 2000;
          overflow: hidden;
          animation: calFadeIn 0.18s ease;
        }

        @keyframes calFadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .calendar-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px 10px;
          background: linear-gradient(135deg, #84cc16 0%, #65a30d 100%);
        }

        .cal-month-label {
          font-size: 14px;
          font-weight: 700;
          color: white;
          letter-spacing: 0.3px;
        }

        .cal-nav-btn {
          background: rgba(255,255,255,0.25);
          border: none;
          color: white;
          font-size: 18px;
          width: 28px;
          height: 28px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
          transition: background 0.15s;
        }

        .cal-nav-btn:hover {
          background: rgba(255,255,255,0.4);
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
          padding: 10px 12px 8px;
        }

        .cal-day-label {
          text-align: center;
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          padding: 4px 0;
          text-transform: uppercase;
        }

        .cal-day {
          text-align: center;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
          padding: 6px 4px;
          border-radius: 8px;
          cursor: default;
          transition: background 0.15s;
        }

        .cal-day:not(.cal-day-empty):hover {
          background: #f3f4f6;
        }

        .cal-day-empty {
          cursor: default;
        }

        .cal-day-today {
          background: #84cc16 !important;
          color: white !important;
          font-weight: 700;
          border-radius: 8px;
        }

        .cal-footer {
          padding: 8px 12px 12px;
          display: flex;
          justify-content: center;
          border-top: 1px solid #f3f4f6;
        }

        .cal-today-btn {
          background: #f7fee7;
          border: 1px solid #bbf7d0;
          color: #65a30d;
          font-size: 12px;
          font-weight: 600;
          padding: 5px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.15s;
        }

        .cal-today-btn:hover {
          background: #dcfce7;
        }

        .page-content {
          background: white;
          border-radius: 16px;
          padding: 24px;
          border: 1px solid rgba(0, 0, 0, 0.04);
          min-height: calc(100vh - 190px);
          box-shadow: 0 6px 12px -2px rgba(132, 204, 22, 0.12), 0 10px 20px -5px rgba(0, 0, 0, 0.05);
          overflow-x: auto;
        }

        .main-footer {
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
          padding: 16px 24px;
          text-align: center;
          margin-top: 20px;
          border-radius: 0 0 16px 16px;
        }

        .main-footer p {
          margin: 0;
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .sidebar { width: 70px; }
          .logo-text, .nav-text, .user-info, .logout-btn span { display: none; }
          .nav-item { justify-content: center; padding: 12px; }
          .main-content { margin-left: 70px; padding: 16px; }
        }

        .notification-bell {
          position: relative;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          border: 2px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        .notification-bell:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.05);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
        }

        .notification-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          background: #dc2626;
          color: white;
          font-size: 11px;
          font-weight: 600;
          padding: 2px 6px;
          border-radius: 10px;
          min-width: 18px;
          text-align: center;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .notifications-dropdown {
          position: absolute;
          top: 90px;
          right: 32px;
          width: 400px;
          max-height: 500px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.2);
          z-index: 1000;
          overflow: hidden;
          border: 1px solid #e5e7eb;
        }

        .notifications-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .notifications-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
        }

        .mark-all-read-btn {
          background: none;
          border: none;
          color: #84cc16;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 6px;
        }

        .mark-all-read-btn:hover {
          background: #f7fee7;
        }

        .notifications-list {
          max-height: 440px;
          overflow-y: auto;
        }

        .notification-item {
          display: flex;
          gap: 12px;
          padding: 16px 20px;
          border-bottom: 1px solid #f3f4f6;
          cursor: pointer;
          transition: background 0.2s ease;
          position: relative;
        }

        .notification-item:hover {
          background: #f9fafb;
        }

        .notification-item.unread {
          background: #f0f9ff;
        }

        .notification-item.unread:hover {
          background: #e0f2fe;
        }

        .notification-icon {
          font-size: 24px;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          border-radius: 10px;
          flex-shrink: 0;
        }

        .notification-content {
          flex: 1;
          min-width: 0;
        }

        .notification-title {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .notification-message {
          font-size: 13px;
          color: #6b7280;
          line-height: 1.4;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .notification-time {
          font-size: 11px;
          color: #9ca3af;
          margin-top: 4px;
        }

        .unread-dot {
          position: absolute;
          right: 20px;
          top: 50%;
          transform: translateY(-50%);
          width: 8px;
          height: 8px;
          background: #3b82f6;
          border-radius: 50%;
        }

        .no-notifications {
          padding: 40px 20px;
          text-align: center;
          color: #9ca3af;
          font-size: 14px;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
        }

        .modal {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 500px;
          box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.2);
          border: 1px solid #e5e7eb;
          animation: slideUp 0.3s ease;
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }

        .modal-body {
          padding: 24px;
          max-height: 60vh;
          overflow-y: auto;
        }

        .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #e5e7eb;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          line-height: 1;
          cursor: pointer;
          color: #6b7280;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          transition: background 0.15s;
        }

        .modal-close:hover {
          background: #f3f4f6;
          color: #111827;
        }

        .notif-modal-icon {
          font-size: 36px;
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f0f9ff;
          border-radius: 16px;
          margin-bottom: 16px;
        }

        .notif-modal-type {
          display: inline-block;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 3px 10px;
          border-radius: 20px;
          background: #f3f4f6;
          color: #6b7280;
          margin-bottom: 8px;
        }

        .notif-modal-title {
          font-size: 18px;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 4px;
        }

        .notif-modal-message {
          font-size: 14px;
          color: #374151;
          line-height: 1.7;
          margin: 0;
        }

        .notif-modal-time {
          font-size: 12px;
          color: #9ca3af;
          margin-top: 16px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .btn-close-notif {
          padding: 10px 24px;
          border: none;
          border-radius: 8px;
          background: #1f2937;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s;
        }

        .btn-close-notif:hover {
          background: #374151;
        }
      `}</style>

      {selectedNotification && (
        <div className="modal-overlay" onClick={() => setSelectedNotification(null)}>
          <div className="modal" style={{ maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '600', color: '#1f2937' }}>
                Notification
              </h2>
              <button className="modal-close" onClick={() => setSelectedNotification(null)} title="Close">×</button>
            </div>
            <div className="modal-body" style={{ padding: '28px 24px 20px' }}>
              <div className="notif-modal-icon">
                {selectedNotification.notification_type === 'password_reset' ? '🔑' :
                 selectedNotification.notification_type === 'payment_due' ? '💰' :
                 selectedNotification.notification_type === 'payment_received' ? '💵' :
                 selectedNotification.notification_type === 'document_verified' ? '✅' :
                 selectedNotification.notification_type === 'document_rejected' ? '❌' :
                 selectedNotification.notification_type === 'membership_expiry' ? '⚠️' :
                 selectedNotification.notification_type === 'leave_approval' ? '📅' :
                 selectedNotification.notification_type === 'broadcast' ? '📢' : '🔔'}
              </div>
              <span className="notif-modal-type">
                {(selectedNotification.notification_type || 'notification').replace(/_/g, ' ')}
              </span>
              <h3 className="notif-modal-title">{selectedNotification.title}</h3>
              <p className="notif-modal-message">{selectedNotification.message}</p>
              <div className="notif-modal-time">
                🕐&nbsp;
                {selectedNotification.created_at
                  ? new Date(selectedNotification.created_at).toLocaleString('en-IN', {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', hour12: true,
                    })
                  : 'N/A'}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-close-notif" onClick={() => setSelectedNotification(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
